import { apiUrl } from "../utils/api";
import { invalidateByPrefix } from "../utils/swrCache";
import { graphMailService } from "./graphMailService";
import { archiviereAbgewieseneNach } from "./mailArchiveRetry";
// P7 (PO 14.09.2026): eine frische Vorgangs-Verknüpfung wird im selben Tab
// sofort gemeldet, damit die Mailliste ihren Marker nachzieht (siehe unten).
import { publiziereZuweisung } from "../components/mail/mailAssignmentBus";

import type { LinkedMailArchiveDto } from "./linkedMailService";

export interface MailLinkDto {
    archive?: LinkedMailArchiveDto;
    id: number;
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /**
     * RFC-Message-Id der Nachricht — bei ZWEI KOPIEN DERSELBEN Mail identisch
     * (die Kopie in „Gesendete Elemente" und die zugestellte tragen dieselbe,
     * aber verschiedene `messageId`). Nur daran sind sie als eine Mail
     * erkennbar; `messageId` ist die Graph-`id` und meint das Exchange-Item.
     * Fehlt bei Bestandszeilen — Aufrufer müssen auf `messageId` zurückfallen.
     */
    internetMessageId?: string | null;
    vorgangId?: number | null;
    todoId?: number | null;
    /**
     * B3 — die MAIL wurde ausdrücklich diesem Kontakt zugewiesen. Nicht zu
     * verwechseln mit dem Hinterlegen der ABSENDERADRESSE am Kontakt: hier
     * wandert nur die Mail in den Schriftverkehr, der Kontakt selbst bleibt
     * unberührt.
     */
    contactId?: number | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    /** „An"-Empfänger (Anzeige-String), Snapshot zum Verknüpfungszeitpunkt (T6). */
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
    linkedByUserId?: string | null;
    linkedAt?: string | null;
    /**
     * Herkunft: `manual` (Default) oder `auto-reply` — der Delta-Poll hat die
     * Antwort einer bereits verlinkten Konversation selbst angehängt.
     */
    source?: string;
}

/**
 * MailLink inkl. aufgelöstem Vorgang — Antwortform von
 * `GET /mail/links?conversationId=…` (State-A-Erkennung „Gehört zu diesem
 * Vorgang", Mail→Vorgang SP1/SP2). Bei `todoId`-Links ist der Vorgang des
 * Todos gejoint; `vorgang: null` = nicht auflösbar (z. B. Todo soft-gelöscht).
 */
export interface MailLinkWithVorgangDto extends MailLinkDto {
    /**
     * E01/J2: Der aufgelöste Vorgang trägt seinen KUNDEN mit — eine Mail am
     * Vorgang gehört automatisch auch zu dessen Kunden. Ohne dieses Feld
     * könnte die rechte Leiste den zugewiesenen Kontakt einer Vorgangs-Mail
     * nicht benennen. `kunde` ist optional, damit ältere Antworten (und
     * Test-Doubles) weiter passen.
     */
    vorgang: {
        id: number;
        titel: string;
        kunde?: { id: number; displayName: string } | null;
        /** Vorgang erledigt — kein Zuweisungsvorschlag mehr. */
        erledigt?: boolean;
    } | null;
    /** Name des direkt zugewiesenen Kontakts (`contactId`). */
    contact?: { id: number; displayName: string } | null;
}

/**
 * Woher der Kontaktbezug einer Mail kommt (Spiegel des Backend-Typs):
 *  - `direkt`  = ausdrücklich diesem Kontakt zugewiesen (B3),
 *  - `vorgang` = über einen Vorgang, dessen Kunde der Kontakt ist (E01),
 *  - `todo`    = über ein To-do, das auf den Kontakt zeigt (Altweg).
 */
export type MailLinkKontaktHerkunft = "direkt" | "vorgang" | "todo";

/** Eine Mail im Schriftverkehr eines Kontakts (F1) samt Vorgangsbezug (F2). */
export interface MailLinkForContactDto extends MailLinkWithVorgangDto {
    herkunft: MailLinkKontaktHerkunft;
}

/**
 * L1/L2 — eine Mail des Postfachs, die zu einem Vorgang gehört, mit dem
 * Snapshot der Verknüpfung. Spiegel des Backend-`VerknuepfteMailItem`.
 *
 * Der Snapshot trägt genug für eine Listenzeile (Betreff, Absender, „An",
 * Datum, Vorschau) — bewusst OHNE Body: den holt erst das Öffnen über
 * `fetchMessageBody`.
 */
export interface VerknuepfteMailItemDto {
    mailbox: string;
    messageId: string;
    conversationId: string | null;
    subject: string | null;
    senderName: string | null;
    senderEmail: string | null;
    /** „An"-Empfänger als Anzeige-String (T6-Snapshot). */
    recipients: string | null;
    /** Empfangszeitpunkt (ISO); ersatzweise der Verknüpfungszeitpunkt. */
    receivedAt: string | null;
    preview: string | null;
    /** Herkunft der Verknüpfung: `manual` | `auto-reply` | `regel`. */
    source: string;
    vorgang: { id: number; titel: string } | null;
}

/**
 * Eine Seite verknüpfter Mails (Keyset). `nextCursor === null` heißt „nichts
 * mehr da" — nur darauf darf ein Leerzustand gestützt werden.
 */
export interface VerknuepfteMailsSeiteDto {
    items: VerknuepfteMailItemDto[];
    nextCursor: string | null;
}

/**
 * P7 (PO 14.09.2026) — Antwort von `POST /mail/links/lookup`: je angefragter
 * `conversationId` die Vorgänge, an denen die Konversation hängt. Eine Id, die
 * in der Antwort FEHLT, hängt an keinem Vorgang, den der Nutzer sehen darf —
 * unsichtbare Vorgänge verschweigt der Server, statt sie als Marker zu
 * verraten. Spiegel des Backend-`VorgangsLookupAntwort`.
 */
export type VorgangsLookupAntwortDto = Record<
    string,
    { vorgaenge: { id: number; titel: string }[] }
>;

export interface CreateMailLinkInput {
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /**
     * RFC-Message-Id der Nachricht — bei ZWEI KOPIEN DERSELBEN Mail identisch
     * (die Kopie in „Gesendete Elemente" und die zugestellte tragen dieselbe,
     * aber verschiedene `messageId`). Nur daran sind sie als eine Mail
     * erkennbar; `messageId` ist die Graph-`id` und meint das Exchange-Item.
     * Fehlt bei Bestandszeilen — Aufrufer müssen auf `messageId` zurückfallen.
     */
    internetMessageId?: string | null;
    vorgangId?: number | null;
    todoId?: number | null;
    /** B3: die Mail dem Kontakt zuweisen (ohne Adressübernahme). */
    contactId?: number | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    /** „An"-Empfänger (Anzeige-String) — Snapshot (T6). */
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
}

/** Eine Nachricht im Batch-Body (F2 Picker-Dialog). `isInInbox` steuert das
 *  optionale Auto-Archiv (advisory; der Server prüft die Sichtbarkeit selbst). */
export interface MailLinkBatchItemInput {
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /**
     * RFC-Message-Id der Nachricht — bei ZWEI KOPIEN DERSELBEN Mail identisch
     * (die Kopie in „Gesendete Elemente" und die zugestellte tragen dieselbe,
     * aber verschiedene `messageId`). Nur daran sind sie als eine Mail
     * erkennbar; `messageId` ist die Graph-`id` und meint das Exchange-Item.
     * Fehlt bei Bestandszeilen — Aufrufer müssen auf `messageId` zurückfallen.
     */
    internetMessageId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    /** „An"-Empfänger (Anzeige-String) — Snapshot (T6). */
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
    isInInbox?: boolean;
}

/** Body für `POST /mail/links/batch` (F2): N Mails an EINEN Vorgang/Todo. */
export interface CreateMailLinkBatchInput {
    vorgangId?: number | null;
    todoId?: number | null;
    /** Posteingang-Nachrichten nach dem Verlinken archivieren (Default true). */
    archiveInbox?: boolean;
    items: MailLinkBatchItemInput[];
}

/** Pro-Item-Status der Batch-Antwort — erlaubt ehrliche Teilfehler-Meldung. */
export interface MailLinkBatchItemResult {
    archive?: LinkedMailArchiveDto | null;
    archiveStored?: boolean;
    archiveId?: number | null;
    messageId: string;
    mailLinkId: number | null;
    created: boolean;
    archived: boolean;
    archiveError: boolean;
    /** Warum der Server nicht archiviert hat, falls sein Guard ablehnte (T-187). */
    archiveCode?: string | null;
    error?: string;
}

/** Body für `POST /mail/assign-to-entity` (AP8/D15): Mail → To-Do-Entwurf. */
export interface AssignMailToEntityInput {
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
    linkedEntityType: "KUNDE" | "VERTRAG" | "RISIKO";
    linkedEntityId: number;
}

export interface AssignMailToEntityResult {
    todoId: number;
    mailLinkId: number;
}

/**
 * P7 (PO 14.09.2026) — eine Mail hängt jetzt an einem Vorgang (direkt oder
 * über ein To-do): im selben Tab sofort melden, je Konversation EINMAL. Die
 * Mailliste holt sich daraufhin den Vorgangsbezug dieser Konversation frisch
 * (`lookupVerknuepft`) — hier ist der Titel des Vorgangs nicht bekannt, und
 * eine Meldung ohne Sichtbarkeitsprüfung wäre auch nicht die Wahrheit.
 *
 * Bewusst ohne `kontakt`: diese Meldung sagt nichts über den Kontakt der
 * Mail, und ein `kontakt: null` hieße im Bus „bewusst ohne Kontakt".
 */
function meldeVorgangsVerknuepfung(
    items: readonly {
        mailbox: string;
        messageId: string;
        conversationId: string | null;
        vorgangId: number | null;
        todoId: number | null;
    }[],
): void {
    const gemeldet = new Set<string>();
    for (const i of items) {
        if (i.vorgangId == null && i.todoId == null) continue;
        const key = `${i.mailbox.trim().toLowerCase()}|${i.conversationId ?? `msg:${i.messageId}`}`;
        if (gemeldet.has(key)) continue;
        gemeldet.add(key);
        publiziereZuweisung({
            mailbox: i.mailbox,
            messageId: i.messageId,
            conversationId: i.conversationId,
            vorgang: { vorgangId: i.vorgangId, todoId: i.todoId },
        });
    }
}

/** Ort der offenen Dokumente-Ansicht: Kategorie und (Alte Kundenakte) Ordner. */
export type MailAblageOrt = { kategorie: string | null; ordner: string | null };

/** Ergebnis der Drop-Ablage (`POST /mail/links` mit `anhaengeMitnehmen`). */
export type MailDropAblage = {
    status: "abgelegt" | "laeuft" | "fehlgeschlagen" | "aus";
    emlDocumentId: number | null;
    anhangDocumentIds: number[];
    laeuft: boolean;
    anhaengeFehlgeschlagen?: number;
    fehler?: string;
};

/**
 * Ein Entwurf lässt sich nicht als Mail zuordnen (Befund 03.11.2026) — der
 * Server antwortet mit 409 und einer Meldung für den Anwender. Die zeigen wir
 * so, wie sie kommt, statt „fehlgeschlagen (409): {…}“.
 */
export function entwurfsMeldung(status: number, txt: string): string | null {
    if (status !== 409) return null;
    try {
        const j = JSON.parse(txt);
        return j?.code === "ENTWURF" && typeof j.message === "string" ? j.message : null;
    } catch {
        return null;
    }
}

export const mailLinkService = {
    async create(input: CreateMailLinkInput): Promise<MailLinkDto> {
        const res = await fetch(apiUrl("mail/links"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(entwurfsMeldung(res.status, txt) ?? `Mail-Zuordnung fehlgeschlagen (${res.status}): ${txt}`);
        }
        // An bestehenden Vorgang/To-Do angehängt → Schriftwechsel-Listen auffrischen.
        invalidateByPrefix("vorgang.");
        invalidateByPrefix("todo.");
        const link: MailLinkDto = await res.json();
        meldeVorgangsVerknuepfung([
            {
                mailbox: input.mailbox,
                messageId: input.messageId,
                conversationId: input.conversationId ?? null,
                vorgangId: input.vorgangId ?? null,
                todoId: input.todoId ?? null,
            },
        ]);
        return link;
    },

    /**
     * F2: mehrere Mails in EINEM Request an einen bestehenden Vorgang/Todo
     * hängen. Dedupt serverseitig und archiviert (bei `archiveInbox`) die als
     * `isInInbox` markierten Nachrichten best-effort. Der Aufrufer verwirft
     * anschließend den Postfach-Cache (invalidateMailbox), wenn archiviert wurde.
     */
    /**
     * Drag & Drop einer Mail (User 01.11.2026): verknüpfen UND sofort samt
     * Anhängen ablegen (`anhaengeMitnehmen`). Reicht das Zeitbudget des Servers
     * nicht (`ablage.laeuft`), fragt derselbe idempotente Aufruf nach — bis zu
     * `versuche`-mal. Nur der Drop-Pfad nutzt das; alle anderen Zuordnungen
     * gehen weiter über {@link create}.
     */
    async createMitAblage(
        input: CreateMailLinkInput,
        versuche = 3,
        /** Drop in die Kontakt-Dokumente: dort sichtbar, wo abgelegt wurde. */
        ablageOrt?: MailAblageOrt | null,
        /**
         * „Nur die Mail“ (User 03.11.2026): sichern und zeigen, die Anhänge
         * aber in der Mail lassen — auch bei der Sicherung im Hintergrund.
         */
        ohneAnhaenge = false,
    ): Promise<MailLinkDto & { ablage?: MailDropAblage }> {
        const body = {
            ...input,
            anhaengeMitnehmen: true,
            ...(ohneAnhaenge ? { ohneAnhaenge: true } : {}),
            ...(ablageOrt ? { ablageOrt } : {}),
        } as CreateMailLinkInput;
        let ergebnis: MailLinkDto & { ablage?: MailDropAblage } = await this.create(body);
        for (let i = 1; i < versuche && ergebnis.ablage?.laeuft; i++) {
            ergebnis = await this.create(body);
        }
        return ergebnis;
    },

    async createBatch(input: CreateMailLinkBatchInput): Promise<{ results: MailLinkBatchItemResult[] }> {
        const res = await fetch(apiUrl("mail/links/batch"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(`Mail-Zuordnung (Batch) fehlgeschlagen (${res.status}): ${txt}`);
        }
        // An bestehenden Vorgang/To-Do angehängt → Schriftwechsel-Listen auffrischen.
        invalidateByPrefix("vorgang.");
        invalidateByPrefix("todo.");
        const out: { results: MailLinkBatchItemResult[] } = await res.json();
        // T-187: Was der Server wegen laufender Sicherung nicht verschieben
        // durfte, über den wartenden Archivieren-Weg nachholen — erst jetzt,
        // nachdem ALLE Links dieses Stapels stehen.
        if (input.archiveInbox !== false && out.results?.length) {
            await archiviereAbgewieseneNach(out.results, input.items, (mailbox, messageId) =>
                graphMailService.archiveMessage(mailbox, messageId),
            );
        }
        // Nur die Nachrichten melden, für die jetzt ein Link steht — ein
        // Item mit Fehler hat keinen bekommen.
        const verlinkt = new Set(
            (out.results ?? []).filter((r) => r.mailLinkId != null).map((r) => r.messageId),
        );
        meldeVorgangsVerknuepfung(
            input.items
                .filter((i) => verlinkt.has(i.messageId))
                .map((i) => ({
                    mailbox: i.mailbox,
                    messageId: i.messageId,
                    conversationId: i.conversationId ?? null,
                    vorgangId: input.vorgangId ?? null,
                    todoId: input.todoId ?? null,
                })),
        );
        return out;
    },

    /**
     * AP8/D15 (a): Mail einer Entität zuweisen → erzeugt serverseitig einen
     * To-Do-ENTWURF (`art='mail'`) und verknüpft die Mail. Der Entwurf erscheint
     * dank AP4 in „Heute" mit Badge.
     */
    async assignToEntity(input: AssignMailToEntityInput): Promise<AssignMailToEntityResult> {
        const res = await fetch(apiUrl("mail/assign-to-entity"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(entwurfsMeldung(res.status, txt) ?? `Zuweisung fehlgeschlagen (${res.status}): ${txt}`);
        }
        // Neuer Entwurf + MailLink → Vorgang-/To-Do-Listen auffrischen.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        // NACHTRAG 2026-07-05: das Backend verschiebt die Mail dabei ins echte
        // Archiv → Posteingangs-/Archiv-Listen dieses Postfachs verwerfen.
        graphMailService.invalidateMailbox(input.mailbox);
        return res.json();
    },

    /**
     * State-A-Erkennung (Mail→Vorgang SP2): alle Links einer Konversation
     * im gegebenen Postfach, inkl. aufgelöstem Vorgang — Basis für „Gehört
     * zu diesem Vorgang" in der Command-Leiste des Mail-Lesens.
     */
    async listByConversation(
        conversationId: string,
        mailbox: string,
    ): Promise<MailLinkWithVorgangDto[]> {
        const res = await fetch(apiUrl("mail/links", { conversationId, mailbox }));
        if (!res.ok) throw new Error(`Vorgang-Zuordnung laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    /**
     * B3 — „Diese MAIL diesem Kontakt zuweisen". Legt NUR die Verknüpfung an;
     * die Absenderadresse wird ausdrücklich NICHT am Kontakt hinterlegt. Der
     * Endpoint ist idempotent — zweimal zuweisen ergibt eine Verknüpfung.
     */
    async createForContact(
        input: CreateMailLinkInput & { contactId: number },
    ): Promise<MailLinkDto> {
        const res = await fetch(apiUrl("mail/links/kontakt"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(entwurfsMeldung(res.status, txt) ?? `Mail dem Kontakt zuweisen fehlgeschlagen (${res.status}): ${txt}`);
        }
        // Der Schriftverkehr des Kontakts hat eine Mail mehr.
        invalidateByPrefix("contact.");
        return res.json();
    },

    /**
     * F1 — Schriftverkehr eines Kontakts: die ihm zugeordneten Mails. Enthält
     * sowohl die ausdrücklich zugewiesenen (B3) als auch die, die über einen
     * Vorgang seines Kunden dazugehören (E01) — je mit aufgelöstem Vorgang (F2).
     */
    async listByContact(contactId: number): Promise<MailLinkForContactDto[]> {
        const res = await fetch(apiUrl(`mail/links/kontakt/${contactId}`));
        if (!res.ok) throw new Error(`Schriftverkehr laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    /**
     * L1/L2 — welche Mails dieses Postfachs gehören zu einem Vorgang?
     * Datenquelle des Kopfzeilen-Filters „Vorgänge": seit 23.08.2026 mit
     * Snapshot und seitenweise, damit der Filter die LISTENQUELLE umschalten
     * kann, statt die geladene Ordnerseite zu sieben (Archiv, Unterordner und
     * alles unterhalb der geladenen Seite fielen dabei durch).
     *
     * `cursor` stammt unverändert aus `nextCursor` der vorigen Antwort und ist
     * für den Aufrufer undurchsichtig.
     */
    async listVerknuepft(
        mailbox: string,
        opts?: { limit?: number; cursor?: string | null },
    ): Promise<VerknuepfteMailsSeiteDto> {
        const res = await fetch(
            apiUrl("mail/links/verknuepft", {
                mailbox,
                ...(opts?.limit ? { limit: String(opts.limit) } : {}),
                ...(opts?.cursor ? { cursor: opts.cursor } : {}),
            }),
        );
        if (!res.ok) throw new Error(`Verknüpfte Mails laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    /**
     * P7 (PO 14.09.2026) — „welche dieser Konversationen gehören zu einem
     * Vorgang?" EIN Aufruf je sichtbarer Listenseite; die Mailliste zeigt
     * damit an jeder Zeile den Vorgang, ohne je Zeile `listByConversation` zu
     * rufen. POST, weil Graph-Konversations-Ids ~150 Zeichen lang sind und
     * 50–100 davon jede URL sprengen. Der Server deckelt auf 200 Ids.
     */
    async lookupVerknuepft(
        mailbox: string,
        conversationIds: readonly string[],
    ): Promise<VorgangsLookupAntwortDto> {
        if (conversationIds.length === 0) return {};
        const res = await fetch(apiUrl("mail/links/lookup"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mailbox, conversationIds }),
        });
        if (!res.ok) throw new Error(`Vorgangs-Zuordnung laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    async listByVorgang(vorgangId: number): Promise<MailLinkDto[]> {
        const res = await fetch(apiUrl("mail/links", { vorgangId }));
        if (!res.ok) throw new Error(`Schriftwechsel laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    async listByTodo(todoId: number): Promise<MailLinkDto[]> {
        const res = await fetch(apiUrl("mail/links", { todoId }));
        if (!res.ok) throw new Error(`Mails laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    /**
     * Eine Verknüpfung innerhalb ihres Vorgangs einem To-do zuordnen — oder mit
     * `null` zurück an den Vorgang. Das Backend erzwingt, dass das Ziel-To-do
     * zu demselben Vorgang gehört (`MailLinkService.setTodo`).
     */
    async setTodo(id: number, todoId: number | null): Promise<MailLinkDto> {
        const res = await fetch(apiUrl(`mail/links/${id}/todo`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ todoId }),
        });
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(`Zuordnung fehlgeschlagen (${res.status})${txt ? `: ${txt}` : ""}`);
        }
        invalidateByPrefix("vorgang.");
        return res.json();
    },

    async remove(id: number): Promise<void> {
        const res = await fetch(apiUrl(`mail/links/${id}`), { method: "DELETE" });
        if (!res.ok) throw new Error(`Mail-Zuordnung entfernen fehlgeschlagen: ${res.status}`);
    },
};
